import cloneDeep from 'lodash/cloneDeep';

import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { pickShiftMaster } from '@/utils/palletList';

interface ShiftPalletListState {
    loading: boolean;
    loaded: boolean;
    fatalError: boolean;

    // источник: 'atom' — факт розлива АТОМ, 'custom' — факт готовой продукции общей смены
    mode: string;
    // факт смены (данные для палетного листа): одна запись — лист по партии,
    // список — лист на всю смену
    documents: any[];
    // мастер смены (работник с ролью voc_roles.is_manager) — предзаполнение
    // «ФИО» палетного листа; пустая строка, если мастер не назначен
    masterName: string;
}

const state: ShiftPalletListState = {
    loading: false,
    loaded: false,
    fatalError: false,

    mode: 'atom',
    documents: [],

    masterName: '',
};

const initState: ShiftPalletListState = cloneDeep(state);

export const PALLET_LIST_FACT = 'PALLET_LIST_FACT';
export const PALLET_LIST_MASTER = 'PALLET_LIST_MASTER';

export const actions: ActionTree<ShiftPalletListState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    /**
     * Данные для палетного листа: продукт (название, штрихкод), тара,
     * номер партии, дата смены, количество из факта.
     * factId — лист по одной партии; documentId (смена) — по всем фактам смены.
     */
    loadData({ commit }, { mode, factId, documentId }) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            // штрихкод и формат — с тары (product_containers), не с продукта
            const columns = mode == 'atom' ? {
                fact_id: 'sfa.id',
                product_name: 'p.name',
                barcode: 'pc.barcode',
                barcode_format: 'pc.barcode_format',
                container_name: 'pc.name',
                container_weight: 'pc.weight',
                number: 'sa.number',
                shift_date: 's.shift_date',
                shift_id: 's.id',
                quantity: 'sfa.quantity',
            } : {
                fact_id: 'scf.id',
                product_name: 'p.name',
                barcode: 'pc.barcode',
                barcode_format: 'pc.barcode_format',
                container_name: 'pc.name',
                container_weight: 'pc.weight',
                number: 'scp.number',
                shift_date: 's.shift_date',
                shift_id: 's.id',
                quantity: 'scf.quantity',
            };

            const where: any[] = documentId
                ? [{$eq: [mode == 'atom' ? 'sa.shift_id' : 'scp.shift_id', {$: documentId}]}]
                : [{$eq: [mode == 'atom' ? 'sfa.id' : 'scf.id', {$: factId}]}];

            const query: any = mode == 'atom' ? {
                from: 's:shift_fact_atom',
                from_as: 'sfa',
                joins: [
                    {
                        join: 's:shift_atom',
                        as: 'sa',
                        on: { $eq: ['sfa.plan_id', 'sa.id'] }
                    },
                    {
                        join: 's:products',
                        as: 'p',
                        on: { $eq: ['sa.product_id', 'p.id'] }
                    },
                    {
                        join: 's:product_containers',
                        as: 'pc',
                        on: { $eq: ['sa.product_container_id', 'pc.id'] }
                    },
                    {
                        join: 's:shifts',
                        as: 's',
                        on: { $eq: ['sa.shift_id', 's.id'] }
                    },
                ],
            } : {
                from: 's:shift_custom_fact',
                from_as: 'scf',
                joins: [
                    {
                        join: 's:shift_custom_plan',
                        as: 'scp',
                        on: { $eq: ['scf.plan_id', 'scp.id'] }
                    },
                    {
                        join: 's:products',
                        as: 'p',
                        on: { $eq: ['scp.product_id', 'p.id'] }
                    },
                    {
                        join: 's:product_containers',
                        as: 'pc',
                        on: { $eq: ['scf.product_container_id', 'pc.id'] }
                    },
                    {
                        join: 's:shifts',
                        as: 's',
                        on: { $eq: ['scp.shift_id', 's.id'] }
                    },
                ],
            };

            vm.$db([
                vm.$q(PALLET_LIST_FACT)
                    .raw('documents.list')
                    .action({
                        columns,
                        page: 1,
                        page_size: documentId ? 1000 : 1,
                        order: [{$asc: [mode == 'atom' ? 'sa.number' : 'scp.number']}],
                        where: {
                            $and: where,
                        },
                        ...query,
                    })
            ]).then((result: ServerResult) => {
                const content = result.getOperation(PALLET_LIST_FACT).getContent();
                const documents = content.documents || [];

                commit('SET_DOCUMENTS', documents);

                // мастер смены — предзаполнение «ФИО»: работники с ролью
                // is_manager (паттерн выборки — ShiftWorkers/_store.ts)
                const shiftId = documentId || (documents[0] && documents[0].shift_id);
                if (!shiftId) {
                    commit('SET_MASTER_NAME', '');
                    commit('LOADED_SUCCESS');
                    resolve(null);
                    return;
                }

                vm.$db([
                    vm.$q(PALLET_LIST_MASTER)
                        .raw('documents.list')
                        .action({
                            columns: {
                                employee_name: 'e.name',
                                role_id: 'role.id',
                                is_manager: 'role.is_manager',
                            },
                            from: 's:shift_workers',
                            from_as: 'sw',
                            joins: [
                                { join: 's:employees', type: 'left', as: 'e', on: { $eq: ['e.id', 'sw.employee_id'] } },
                                { join: 's:voc_roles', type: 'left', as: 'role', on: { $eq: ['role.id', 'sw.role_id'] } },
                            ],
                            order: [{$asc: ['role.id']}, {$asc: ['sw.id']}],
                            page: 1,
                            page_size: 200,
                            where: { $and: [{$eq: ['sw.shift_id', {$: shiftId}]}] },
                        })
                ]).then((masterResult: ServerResult) => {
                    const workers = masterResult.getOperation(PALLET_LIST_MASTER).getContent().documents || [];
                    commit('SET_MASTER_NAME', pickShiftMaster(workers));
                    commit('LOADED_SUCCESS');
                    resolve(null);
                }).catch(() => {
                    // ФИО не критично: лист открывается с пустым полем
                    commit('SET_MASTER_NAME', '');
                    commit('LOADED_SUCCESS');
                    resolve(null);
                });
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },
};

export const mutations: MutationTree<ShiftPalletListState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = (initState as any)[key]
        }
    },

    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_DOCUMENTS(state, documents) {
        state.documents = documents;
    },

    SET_MASTER_NAME(state, name) {
        state.masterName = name || '';
    },
};

export const STORE_KEY = '$_shift_pallet_list';

export const store = {
    namespaced: true,
    state,
    actions,
    mutations,
};
